import axios from 'axios';
import { setupAxiosInterceptor } from './auth/axiosConfig';
import config from './config';

// API instance for Linear agent integration endpoints
const linearApi = axios.create({
  baseURL: config.API_BASE_URL + '/cellcog/linear',
});

setupAxiosInterceptor(linearApi);

/**
 * Initiate Linear OAuth connection.
 * Returns an auth_url to redirect the user to Linear for authorization.
 * @returns {Promise<{data: {auth_url: string, state: string, already_connected: boolean, message: string}}>}
 */
export const connectLinear = () => {
  return linearApi.post('/connect');
};

/**
 * List the current user's Linear installations (workspaces they've connected).
 * @returns {Promise<{data: Array<InstallationResponse>}>}
 *
 * Each installation looks like:
 *   {
 *     id, linear_org_id, linear_org_name, is_active, scopes,
 *     team_count, can_access_all_public_teams,
 *     member_count, workspace_members_synced_at,
 *     installed_at, updated_at
 *   }
 */
export const listLinearInstallations = () => {
  return linearApi.get('/installations');
};

/**
 * Disconnect a Linear installation (marks it inactive; errors out active sessions).
 * @param {string} installationId - The CellCog installation ID (not the Linear org ID)
 * @returns {Promise<{data: {success: boolean, message: string}}>}
 */
export const disconnectLinearInstallation = (installationId) => {
  return linearApi.delete(`/installations/${installationId}`);
};

/**
 * Helper: parse and strip the `?linear=connected&org=<name>` query params
 * from the URL after returning from the OAuth callback. Returns the org name
 * (if any) and always cleans up the URL.
 * @returns {string|null} The org name if linear=connected was present, otherwise null.
 */
export const parseLinearCallbackParams = () => {
  const params = new URLSearchParams(window.location.search);
  const linearStatus = params.get('linear');
  const orgName = params.get('org');

  if (linearStatus) {
    // Clean URL regardless of success/error
    const url = new URL(window.location);
    url.searchParams.delete('linear');
    url.searchParams.delete('org');
    window.history.replaceState({}, document.title, url.toString());
  }

  return linearStatus === 'connected' ? (orgName || '') : null;
};
